import { useContext, useEffect } from 'react';
import Button from '../../../../../../components/Button';
import { CollapsibleAreaContext } from '../../../../../../components/CollapsibleArea';
import Detail from '../../../../../../components/Detail';
import ExternalLink from '../../../../../../components/ExternalLink';
import { config } from '../../../../../../config/config';
import useIsMounted from '../../../../../../hooks/useIsMounted';
import IncentivizeSpool from '../SpoolCreatorFeesAccordionRow/IncentivizeSpool';
import SpoolCreatorFeesDetails from './SpoolCreatorFeesDetails';
import { SpoolDto } from '../../../../../../../types/spool-local';
import { formatAmountWithSuffix, formatUSD } from '../../../../../../helpers/formats';
import CopyPopUp from '../../../../../../components/CopyPopUp';
import { useWeb3React } from '@web3-react/core';
import { SupportedChains } from '../../../../../../config/constants';

type Props = {
    spoolDetails: SpoolDto;
    collapsed: boolean;
};

const SpoolCreatorFeesAccordionContent: React.FC<Props> = ({
    spoolDetails,
    collapsed,
}) => {
    const {
        address,
        name,
        performanceFees,
        tvr,
        incentivizedRewards,
        ownerFeesCollectedAmount,
    } = spoolDetails;
    const { updateCollapsedHeight } = useContext(CollapsibleAreaContext);

    const isMounted = useIsMounted();

    const { chainId } = useWeb3React();

    useEffect(() => {
        const interval = setInterval(() => {
            isMounted() && updateCollapsedHeight();
        }, 100);

        const timeout = setTimeout(() => {
            clearInterval(interval);
        }, 500);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [collapsed, isMounted, updateCollapsedHeight]);

    return (
        <>
            <span className='note'>Data is recent but may not be up to date.</span>
            <div className='details'>
                <Detail
                    title='TVR'
                    value={`${
                        tvr === '0' ? 'N/A' : `~$${formatAmountWithSuffix(tvr || 0)}`
                    }`}
                />
                {chainId !== SupportedChains.ARBITRUM_ONE && (
                    <>
                        <Detail
                            title='My Creator Fee'
                            value={`${formatUSD(performanceFees.owner || 0)}%`}
                        />
                        <Detail
                            title='Fees Earned Since Creation'
                            value={`~$${formatUSD(ownerFeesCollectedAmount || 0)}`}
                        />
                    </>
                )}
                {/* <Chart data={data} aspect={getAspectRatio()} /> */}
            </div>
            <div className='urls'>
                <div className='url'>
                    {/* TODO: Implement Copy */}
                    <CopyPopUp
                        component={<Button>Copy URL</Button>}
                        text={`${config.urls.spoolApp}/${
                            config.urls.spoolPath
                        }/${address}${
                            chainId === SupportedChains.ARBITRUM_ONE
                                ? '?network=arbitrum'
                                : ''
                        }`}
                    />

                    <p>
                        Spool URL :{' '}
                        <ExternalLink
                            to={`${config.urls.spoolApp}/${
                                config.urls.spoolPath
                            }/${address}${
                                chainId === SupportedChains.ARBITRUM_ONE
                                    ? '?network=arbitrum'
                                    : ''
                            }`}
                        >
                            {name}
                        </ExternalLink>
                    </p>
                </div>
                <div className='url'>
                    <Button externalLink={config.urls.sdk}>View SDK</Button>
                    <p>Use the Spool SDK to connect this Spool to your website or dApp</p>
                </div>
            </div>
            <SpoolCreatorFeesDetails spoolDetails={spoolDetails} />
            <IncentivizeSpool
                spoolDetails={spoolDetails}
                incentivizedRewards={incentivizedRewards}
            />
        </>
    );
};

export default SpoolCreatorFeesAccordionContent;
